<template>
  <!--
    A11Y-REF (WCAG/WAI)
    - WCAG 1.3.1 Info and Relationships: use native list semantics (ul/li) for item grouping.
    - WCAG 4.1.2 Name, Role, Value: item accessible name is delegated via itemAriaLabel.
    - WCAG 2.5.3 Label in Name: keep visible/spoken label aligned when custom labels are provided.
    - WAI-ARIA Authoring Practices: prefer native HTML semantics over custom list roles.
  -->
  <section
    class="card-list def-card-list"
    v-bind="rootAttrs"
  >
    <ul
      v-if="hasItems"
      class="card-list__list"
      v-bind="resolvedListAttrs"
    >
      <li
        v-for="(item, index) in items"
        :key="resolveKey(item, index)"
        class="card-list__item"
        v-bind="resolveItemAttrs(item, index)"
      >
        <slot
          name="item"
          :item="item"
          :index="index"
          :isLast="index === items.length - 1"
          :ariaLabel="resolveAriaLabel(item, index)"
          :itemAttrs="resolveItemAttrs(item, index)"
          :onClick="(event) => emitItemClick(item, index, event)"
        />
      </li>
    </ul>

    <div
      v-else
      class="card-list__empty"
      :role="emptyRole || null"
      :aria-live="emptyAriaLive || null"
      aria-atomic="true"
    >
      <slot name="empty" :emptyText="emptyText">
        <p v-if="emptyText" class="card-list__empty-message">{{ emptyText }}</p>
      </slot>
    </div>
  </section>
</template>

<script>
export default {
  name: "CardList",
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    listAriaLabel: {
      type: String,
      default: "",
    },
    listAriaLabelledby: {
      type: String,
      default: "",
    },
    emptyText: {
      type: String,
      default: "",
    },
    emptyRole: {
      type: String,
      default: "status",
    },
    emptyAriaLive: {
      type: String,
      default: "polite",
    },
    // A11Y-REF WCAG 4.1.2 / 2.5.3: consumer provides accessible name per item.
    itemAriaLabel: {
      type: Function,
      default: null,
    },
    itemKey: {
      type: Function,
      default: null,
    },
    listAttrs: {
      type: [Function, Object],
      default: null,
    },
    itemAttrs: {
      type: [Function, Object],
      default: null,
    },
  },
  computed: {
    hasItems() {
      return Array.isArray(this.items) && this.items.length > 0;
    },
    rootAttrs() {
      return {
        ...this.$attrs,
        "aria-label": this.listAriaLabel || null,
        "aria-labelledby": this.listAriaLabelledby || null,
      };
    },
    resolvedListAttrs() {
      return this.normalizeAttrs(this.listAttrs);
    },
  },
  methods: {
    emitItemClick(item, index, event) {
      this.$emit("item-click", item, index, event);
    },
    resolveAriaLabel(item, index) {
      if (!this.itemAriaLabel) {
        return null;
      }
      return this.itemAriaLabel(item, index);
    },
    normalizeAttrs(source, item, index) {
      if (!source) {
        return {};
      }
      if (typeof source === "function") {
        return source(item, index) || {};
      }
      return source;
    },
    resolveItemAttrs(item, index) {
      return this.normalizeAttrs(this.itemAttrs, item, index);
    },
    resolveKey(item, index) {
      if (this.itemKey) {
        return this.itemKey(item, index);
      }
      if (item == null) {
        return index;
      }
      if (typeof item !== "object") {
        return item;
      }
      return item.guid || item.id || item._id || item.uuid || item.key || item.code || index;
    },
  },
};
</script>

